import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format } from 'date-fns'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { DatePicker } from '@/components/date-picker'
import { PaginatedSelect } from '@/components/paginated-select'
import { get_cases } from '@/api/case'
import { get_users } from '@/api/user'
import type { Expense, ExpenseType, ExpenseCategory } from '@/api/expenses'

const CATEGORIES: ExpenseCategory[] = [
  'frais_justice',
  'deplacement',
  'expertise',
  'honoraires_confrere',
  'fournitures',
  'communication',
  'other',
]

const formSchema = z
  .object({
    type: z.enum(['cabinet', 'dossier']),
    category: z.string().min(1),
    description: z.string().min(1),
    amount: z
      .string()
      .min(1)
      .refine((v) => !isNaN(parseFloat(v)) && parseFloat(v) > 0),
    expense_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    billable: z.boolean(),
    case_id: z.string().optional(),
    case_name: z.string().optional(),
    assigned_to: z.string().optional(),
    assigned_to_name: z.string().optional(),
  })
  .refine(
    (d) => {
      if (d.type === 'dossier') return !!d.case_id
      return true
    },
    { message: 'case_required', path: ['case_id'] },
  )

type FormValues = z.infer<typeof formSchema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  expense?: Expense | null
  defaultType?: ExpenseType
  defaultCaseId?: number
  defaultCaseName?: string
  hideCaseField?: boolean
  onSaved: (values: {
    type: ExpenseType
    category: ExpenseCategory
    description: string
    amount: string
    expense_date: string
    billable: boolean
    case?: number
    assigned_to?: string
  }) => Promise<void>
}

export function ExpenseFormDialog({
  open,
  onOpenChange,
  expense,
  defaultType = 'cabinet',
  defaultCaseId,
  defaultCaseName,
  hideCaseField = false,
  onSaved,
}: Props) {
  const { t } = useTranslation()
  const isEdit = !!expense

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: defaultType,
      category: 'other',
      description: '',
      amount: '',
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      billable: false,
      case_id: defaultCaseId ? String(defaultCaseId) : undefined,
      case_name: defaultCaseName ?? undefined,
      assigned_to: undefined,
      assigned_to_name: undefined,
    },
  })

  useEffect(() => {
    if (!open) return
    if (isEdit && expense) {
      form.reset({
        type: expense.type,
        category: expense.category,
        description: expense.description,
        amount: expense.amount,
        expense_date: expense.expense_date,
        billable: expense.billable,
        case_id: expense.case ? String(expense.case) : undefined,
        case_name: expense.case_title ?? undefined,
        assigned_to: expense.assigned_to?.id ?? undefined,
        assigned_to_name: expense.assigned_to?.name ?? undefined,
      })
    } else {
      form.reset({
        type: defaultType,
        category: 'other',
        description: '',
        amount: '',
        expense_date: format(new Date(), 'yyyy-MM-dd'),
        billable: false,
        case_id: defaultCaseId ? String(defaultCaseId) : undefined,
        case_name: defaultCaseName ?? undefined,
        assigned_to: undefined,
        assigned_to_name: undefined,
      })
    }
  }, [open])

  const watchType = form.watch('type')

  const fetchCaseItems = useCallback(async (page: number, search?: string) => {
    const result = await get_cases({
      page,
      page_size: 20,
      ordering: null,
      name: search || null,
    })
    return {
      items: result.results.map((c) => ({ id: c.id, name: c.name })),
      hasMore: !!result.next,
    }
  }, [])

  const fetchUserItems = useCallback(async (page: number, search?: string) => {
    const list = await get_users({
      page: (page - 1) * 20,
      page_size: 20,
      ordering: null,
      name: search || null,
      is_active: null,
      roles: null,
    })
    return { items: list.results, hasMore: list.next != null }
  }, [])

  const handleSubmit = async (values: FormValues) => {
    await onSaved({
      type: values.type as ExpenseType,
      category: values.category as ExpenseCategory,
      description: values.description,
      amount: values.amount,
      expense_date: values.expense_date,
      billable: values.billable,
      case: values.case_id ? parseInt(values.case_id, 10) : undefined,
      assigned_to: values.assigned_to || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange(false) }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('exp_edit_title') : t('exp_create_title')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('exp_edit_desc') : t('exp_create_desc')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">

            {/* Type: selector in create mode, read-only badge in edit mode */}
            {!hideCaseField && (
              isEdit ? (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('exp_field_type')}</p>
                  <Badge variant="secondary">
                    {t(`exp_type_${expense!.type}`)}
                  </Badge>
                </div>
              ) : (
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('exp_field_type')}</FormLabel>
                      <Select
                        onValueChange={(v) => {
                          field.onChange(v)
                          if (v === 'cabinet') {
                            form.setValue('case_id', undefined)
                            form.setValue('case_name', undefined)
                            form.setValue('billable', false)
                          }
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="cabinet">{t('exp_type_cabinet')}</SelectItem>
                          <SelectItem value="dossier">{t('exp_type_dossier')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )
            )}

            {/* Case: picker in create mode, read-only text in edit mode */}
            {watchType === 'dossier' && !hideCaseField && (
              isEdit ? (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('exp_field_case')}</p>
                  <p dir="auto" className="text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted/40">
                    {expense?.case_title ?? '—'}
                  </p>
                </div>
              ) : (
                <FormField
                  control={form.control}
                  name="case_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('exp_field_case')}</FormLabel>
                      <FormControl>
                        <PaginatedSelect
                          placeholder={t('select_case')}
                          fetchItems={fetchCaseItems}
                          value={
                            field.value && form.watch('case_name')
                              ? { id: field.value, name: form.watch('case_name') as string }
                              : null
                          }
                          onSelect={(item) => {
                            field.onChange(item ? String(item.id) : undefined)
                            form.setValue('case_name', item?.name ?? undefined)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )
            )}

            {/* Category */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('exp_field_category')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t(`exp_cat_${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('exp_field_description')}</FormLabel>
                  <FormControl>
                    <Input dir="auto" placeholder={t('exp_field_description')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount — MAD hardcoded */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('exp_field_amount')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="pr-14"
                        {...field}
                      />
                      <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
                        MAD
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date */}
            <FormField
              control={form.control}
              name="expense_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('exp_field_date')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      date={field.value ? new Date(field.value) : undefined}
                      setDate={(d) => field.onChange(d ? format(d, 'yyyy-MM-dd') : '')}
                      placeholder={t('select_date')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Assigned to (optional) */}
            <FormField
              control={form.control}
              name="assigned_to"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('exp_field_assigned_to')}</FormLabel>
                  <FormControl>
                    <PaginatedSelect
                      placeholder={t('exp_field_assigned_to_placeholder')}
                      fetchItems={fetchUserItems}
                      value={
                        field.value && form.watch('assigned_to_name')
                          ? { id: field.value, name: form.watch('assigned_to_name') as string }
                          : null
                      }
                      onSelect={(item) => {
                        field.onChange(item ? String(item.id) : undefined)
                        form.setValue('assigned_to_name', item?.name ?? undefined)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Billable — only for dossier type */}
            {watchType === 'dossier' && (
              <FormField
                control={form.control}
                name="billable"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <FormLabel className="cursor-pointer">
                      {t('exp_field_billable')}
                    </FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? t('common.saving')
                  : isEdit
                    ? t('edit')
                    : t('exp_create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
